import './apis/index';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React, { Suspense, lazy } from 'react';
import { AuthProvider } from './contexts';
import reportWebVitals from './reportWebVitals';
import {
	Route,
	Navigate,
	createBrowserRouter,
	createRoutesFromElements,
	RouterProvider,
} from 'react-router-dom';
import { CookiesProvider } from 'react-cookie';
import ReactDOM from 'react-dom/client';
import { ToastContainer } from 'react-toastify';
import {
	Lead1,
	Lead2,
	LoadingBackdrop,
	NewJobHistorySuccess,
	ReferenceStatus,
	Settings,
	TemplateBuilderHome,
} from './components';
import CandidateRequestCheck from './components/CandidateRequestCheck';
import JobHistoryRequestCheck from './components/JobHistoryRequestCheck';
import EditJobInformation from './components/EditJobInformation';
import JobHistory from './components/JobHistory';
import JobInformation from './components/JobInformation';
import JobSummary from './components/JobSummary';
import QuestionnaireData from './components/QuestionnaireData';
import RefereeBegin from './components/RefereeBegin';
import RefereeDecline from './components/RefereeDecline';
import RefereeQuestionnaire from './components/RefereeQuestionnaire';
import RefereeReview from './components/RefereeReview';
import RefereeBasics from './components/RefereeVerify';
import TBSummary from './components/TemplateBuilder_Summary';
import {
	AdminLogin,
	AdminRegister,
	AuthMessage,
	ForgotPassword,
	ResetPassword,
	ReVerifyEmail,
	SignIn,
	SignUp,
	SuperAdminLogin,
	SuperAdminRecoverAcc,
	UserVerified,
} from './pages/auth';
import TermsAndConditions from './pages/auth/TermsAndConditions';
import PrivacyPolicy from './pages/user/PrivacyPolicy';
import RedirectComponent from './pages/user/RedirectComponent';
import User from './pages/user/User';

//Admin console routes import.
import AdminConsole from './pages/admin-console';
import Reports from './pages/admin-console/Reports';
import SuperAdmins from './pages/admin-console/Admins';
import SuperAdminClients from './pages/admin-console/Clients';
import SuperAdminUsers from './pages/admin-console/Users';
import SuperAdminSettings from './pages/admin-console/Settings';
import SuperAdminAuditLog from './pages/admin-console/AuditLog';
import SuperAdminAddNewClient from './pages/admin-console/AddNewClient';
import SuperAdminEditClientInfo from './pages/admin-console/EditClientInfo';
import SuperAdminAddNewUser from './pages/admin-console/AddNewUser';
import SuperAdminAddNewAdmin from './pages/admin-console/AddNewAdmin';
import EditAdminInfo from './pages/admin-console/EditAdminInfo';
import EditUserInfo from './pages/admin-console/EditUserInfo';

//client-admin routes import.
import ClientAdmin from './pages/client-admin';
import AddNewAdmin from './pages/client-admin/AddNewAdmin';
import AddNewUser from './pages/client-admin/AddNewUser';
import Admins from './pages/client-admin/Admins';
import EditAdminUserInfo from './pages/client-admin/EditAdminUserInfo';
import EditClientUserInfo from './pages/client-admin/EditClientUserInfo';
import AdminSettings from './pages/client-admin/Settings';
import AdminUsers from './pages/client-admin/Users';

//IMPORTANT: DO NOT CHANGE CSS ORDER. IT WILL CAUSE CASCADING
import './tailwind-import.css';
import './index.css';
import 'react-toastify/dist/ReactToastify.css';
import './assets/css/material-credibled.css';
import './assets/css/custom_credibled.css';
import './components/RefSum.css';
import {
	ThemeProvider,
	Theme,
	StyledEngineProvider,
	createTheme,
} from '@mui/material/styles';
import orange from '@mui/material/colors/orange';

import Requests from './pages/user/Requests';
import NewRequest from './pages/user/NewRequest';
import Questionnaires from './components/Questionnaires';
import TemplateBuilder from './components/TemplateBuilder_Main';
import CandidateSummary from './components/CandidateSummary';
import setAuthorizationBearerToken, {
	getBackgroundCheck,
} from './apis/user.api';
import { BackgroundCheckRequests } from './pages/user/background-check/types';
import { BackgroundCheckResultPage } from './pages/user';
import Loading from './components/Loading';
import CssBaseline from '@mui/material/CssBaseline/CssBaseline';
const BackgroundCheck = lazy(() => import('./pages/user/background-check'));
const GenericRedirectComponent = lazy(
	() => import('./components/GenericRedirectComponent')
);

declare module '@mui/styles/defaultTheme' {
	interface DefaultTheme extends Theme {}
}

declare module '@mui/styles/defaultTheme' {
	interface DefaultTheme extends Theme {}
}

export const queryClient = new QueryClient();
declare module '@mui/material/styles' {
	interface Theme {
		status: {
			danger: string;
		};
	}

	interface ThemeOptions {
		status?: {
			danger?: string;
		};
	}

	interface MuiCircularProgress {
		defaultProps: {
			color: any;
		};
	}
}

const theme = createTheme({
	palette: {
		primary: {
			main: '#ed642b',
		},
		secondary: {
			main: '#250c77',
		},
	},
	status: {
		danger: orange[500],
	},
	typography: {
		button: {
			textTransform: 'none',
			fontWeight: 400,
		},
	},
});

const router = createBrowserRouter(
	createRoutesFromElements(
		<>
			<Route
				path='/signin'
				element={<SignIn />}
			/>
			<Route
				path='/'
				element={
					<Suspense fallback={<LoadingBackdrop />}>
						<GenericRedirectComponent />
					</Suspense>
				}
			/>
			<Route
				path='/signup'
				element={<SignUp />}
			/>
			<Route
				path='/forgot-password'
				element={<ForgotPassword />}
			/>
			<Route
				path='/reset-password'
				element={<ResetPassword />}
			/>
			<Route
				path='/verify-email/:email/:name/:token'
				element={<UserVerified />}
			/>
			<Route
				path='/success'
				element={<AuthMessage />}
			/>
			<Route
				path='/reverify-email'
				element={<ReVerifyEmail />}
			/>
			<Route
				path='/admin-login'
				element={<SuperAdminLogin />}
			/>
			<Route
				path='/admin-recover-account'
				element={<SuperAdminRecoverAcc />}
			/>
			<Route
				path='/template-builder/summary'
				element={<TBSummary />}
			/>
			<Route
				path={'/home/*'}
				element={<User />}
				loader={() => setAuthorizationBearerToken()}
			>
				<Route
					path='requests'
					element={<Requests />}
				/>
				<Route
					path='add-new-request'
					element={<NewRequest />}
				/>
				<Route
					path='questionnaires'
					element={<Questionnaires />}
				/>
				<Route
					path='template-homepage'
					element={<TemplateBuilderHome />}
				/>
				<Route
					path='settings'
					element={<Settings />}
				/>
				<Route
					path='template-builder'
					element={<TemplateBuilder />}
				/>
				<Route
					path='template-builder/:questionnaireUUID'
					element={<TemplateBuilder />}
				/>
				<Route
					path='candidate/summary/:id'
					element={<CandidateSummary />}
				/>
				<Route
					path='reference/status/:candidateId/:jobId'
					element={<ReferenceStatus />}
				/>
				<Route
					path='background-check'
					element={
						<Suspense fallback={<LoadingBackdrop />}>
							<BackgroundCheck />
						</Suspense>
					}
					loader={async () => {
						const response = (await getBackgroundCheck({})) as {
							results: BackgroundCheckRequests;
						};
						return response.results.sort((a, b) => {
							const dateA = a.webhook_hit_at ? a.webhook_hit_at : a.created_at;
							const dateB = b.webhook_hit_at ? b.webhook_hit_at : b.created_at;
							return new Date(dateA) > new Date(dateB) ? 1 : -1;
						});
					}}
				/>
				<Route
					path='background-check/:uuid'
					element={
						<Suspense fallback={<LoadingBackdrop />}>
							<BackgroundCheckResultPage />
						</Suspense>
					}
				/>
				<Route
					path='*'
					element={<RedirectPage />}
				/>
			</Route>
			<Route
				path='/terms-and-conditions'
				element={<TermsAndConditions />}
			/>
			<Route
				path='/candidate-job-history/:name/:id'
				element={<CandidateRequestCheck />}
			/>
			<Route
				path='/job-info/:name/:id'
				element={<JobInformation />}
			/>
			<Route
				path='/edit-job-info/:name/:id/:candidateHash'
				element={<EditJobInformation />}
			/>
			<Route
				path='/job-history/:name/:id'
				element={<JobHistory />}
			/>
			<Route
				path='/jobHistory/:candidateName/:refereeName/:id'
				element={<JobHistoryRequestCheck />}
			/>
			<Route
				path='/referee-accept/:candidateName/:refereeName/:id'
				element={<RefereeBegin />}
			/>
			<Route
				path='/referee-verify/:candidateName/:refereeName/:id'
				element={<RefereeBasics />}
			/>
			<Route
				path='/referee-questionnaire/:candidateName/:refereeName/:id'
				element={<RefereeQuestionnaire />}
			/>
			<Route
				path='/lead-to-hire/:candidateName/:refereeName/:id'
				element={<Lead1 />}
			/>
			<Route
				path='/lead-to-job/:candidateName/:refereeName/:id'
				element={<Lead2 />}
			/>
			<Route
				path='/referee-summary/:candidateName/:refereeName/:id'
				element={<NewJobHistorySuccess />}
			/>
			<Route
				path='/job-summary/:name/:id'
				element={<JobSummary />}
			/>
			<Route
				path='/job-history-complete/:candidateName/:id'
				element={<NewJobHistorySuccess />}
			/>
			<Route
				path='/referee-review/:name/:candidateHash/:refereeHash'
				element={<RefereeReview />}
			/>
			<Route
				path='/jobHistory/decline/:candidateName/:refereeName/:id'
				element={<RefereeDecline />}
			/>
			<Route
				path='/questionnaires/questionnaireData'
				element={<QuestionnaireData />}
			/>
			<Route
				path={'/privacy-policy'}
				element={<PrivacyPolicy />}
			/>
			<Route
				path={'/reverification/:UUID'}
				element={<RedirectComponent />}
			/>
			<Route
				path={'/register/:UUID'}
				element={<RedirectComponent />}
			/>
			<Route
				path={'/password-reset/:UUID'}
				element={<RedirectComponent />}
			/>
			<Route
				path={'/admin/login'}
				element={<AdminLogin />}
			/>
			<Route
				path={'/admin/register'}
				element={<AdminRegister />}
			/>
			<Route
				path='/super-admin/*'
				element={<AdminConsole />}
			>
				<Route
					path={'reports'}
					element={<Reports />}
				/>
				<Route
					path={'admins'}
					element={<SuperAdmins />}
				/>
				<Route
					path={'admins/add-new-admin'}
					element={<SuperAdminAddNewAdmin />}
				/>
				<Route
					path={'admins/edit-admin-info/:email'}
					element={<EditAdminInfo />}
				/>
				<Route
					path={'users/edit-user-info/:email'}
					element={<EditUserInfo />}
				/>
				<Route
					path={'clients'}
					element={<SuperAdminClients />}
				/>
				<Route
					path={'clients/add-new-client'}
					element={<SuperAdminAddNewClient />}
				/>
				<Route
					path={'clients/edit-client-info/:uuid'}
					element={<SuperAdminEditClientInfo />}
				/>
				<Route
					path={'users'}
					element={<SuperAdminUsers />}
				/>
				<Route
					path={'users/add-new-user'}
					element={<SuperAdminAddNewUser />}
				/>
				<Route
					path={'settings'}
					element={<SuperAdminSettings />}
				/>
				<Route
					path={'audit-log'}
					element={<SuperAdminAuditLog />}
				/>
				<Route
					path={''}
					element={<Navigate to='clients' />}
				/>
			</Route>
			<Route
				path={'/admin/*'}
				element={<ClientAdmin />}
			>
				<Route
					path='users'
					element={<AdminUsers />}
				/>
				<Route
					path='users/edit-user-info/:email'
					element={<EditClientUserInfo />}
				/>
				<Route
					path='admins/edit-admin-info/:email'
					element={<EditAdminUserInfo />}
				/>
				<Route
					path='users/add-new-user'
					element={<AddNewUser />}
				/>
				<Route
					path='settings'
					element={<AdminSettings />}
				/>
				<Route
					path='admins/add-new-admin'
					element={<AddNewAdmin />}
				/>
				<Route
					path='admins'
					element={<Admins />}
				/>
				<Route
					path={''}
					element={<Navigate to='users' />}
				/>
			</Route>
			<Route element={<RedirectComponent />} />
		</>
	)
);

const root = ReactDOM.createRoot(document.getElementById('root')!);
root.render(
	<QueryClientProvider client={queryClient}>
		<CssBaseline />
		<ReactQueryDevtools initialIsOpen={false} />
		<CookiesProvider>
			<StyledEngineProvider injectFirst>
				<ThemeProvider theme={theme}>
					<AuthProvider>
						{process.env.REACT_APP_CREATION_MODE ? (
							<Loading />
						) : (
							<RouterProvider router={router} />
						)}
					</AuthProvider>
				</ThemeProvider>
			</StyledEngineProvider>
		</CookiesProvider>
		<ToastContainer
			theme='colored'
			closeOnClick
			closeButton
		/>
	</QueryClientProvider>
);

reportWebVitals();

function RedirectPage() {
	return <Navigate to='/home/requests' />;
}
